import React, { useEffect, useState, useContext, useMemo } from "react";
import { collection, getDocs, getDoc, doc, query, where, setDoc } from "firebase/firestore";
import { useParams, Link } from "react-router-dom";
import { db } from "../../firebase";
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { tiendasColumns } from "../../datatablesource";
import { AuthContext } from "../../context/AuthContext";
import customLocaleText from './LocaleText.js'
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import StoreMallDirectoryRoundedIcon from '@mui/icons-material/StoreMallDirectoryRounded';
import ArrayCellRenderer from "../../ArrayCellRenderer.js";
import Button from '@mui/material/Button';
import { ToggleButton, ToggleButtonGroup, Box, Typography } from '@mui/material';
import datatableProfiler from './utils/DatatablePerformanceProfiler';

const DatatableTiendasCliente = () => {
  const { clienteId, nombreCliente } = useParams();
  const [tiendas, setTiendas] = useState([]);
  const [filter, setFilter] = useState("");
  const [clienteImg, setClienteImg] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [recoveredItems, setRecoveredItems] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [emails, setEmails] = useState({});
  const [displayMode, setDisplayMode] = useState('nombre');

  const { currentService, adminData } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleDisplayModeChange = (event, newMode) => {
    if (newMode !== null) {
      setDisplayMode(newMode);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (currentService.id) {
        // 📊 Iniciar sesión de profiling
        datatableProfiler.startSession('DatatableTiendas_FetchData', {
          serviceId: currentService.id,
          clienteId,
          nombreCliente,
          esAdministrador: adminData.tipoUsuario?.includes("administrador"),
          tiendasAsignadas: adminData.tiendas?.length || 0
        });

        try {
          console.log("Tipos de usuario:", adminData.tipoUsuario);
          console.log("¿Es administrador?:", adminData.tipoUsuario?.includes("administrador"));
          let tiendasQuery;
          
          // Check if user has admin role
          if (adminData.tipoUsuario?.includes("administrador")) {
            // 📊 Medir construcción de query
            datatableProfiler.startMeasurement('07a_BuildQuery', {
              clienteId
            });

            // Admin can see all stores for the client without filtering by assigned stores
            console.log("Realizando consulta de administrador");
            const todasLasTiendasRef = collection(db, "servicios", currentService.id, "tiendas");
            tiendasQuery = query(
              todasLasTiendasRef,
              where("cliente", "==", clienteId)
            );

            datatableProfiler.endMeasurement('07a_BuildQuery', {
              queryBuilt: true
            });
            
            // 📊 Medir ejecución de query a Firestore
            datatableProfiler.startMeasurement('07b_ExecuteQuery', {
              clienteId
            });

            const tiendasSnapshot = await getDocs(tiendasQuery);

            datatableProfiler.endMeasurement('07b_ExecuteQuery', {
              docsReturned: tiendasSnapshot.size,
              snapshotSize: tiendasSnapshot.docs.reduce((sum, doc) => 
                sum + JSON.stringify(doc.data()).length, 0
              )
            });

            // 📊 Medir procesamiento de datos
            datatableProfiler.startMeasurement('07c_ProcessData', {
              docsToProcess: tiendasSnapshot.size
            });

            const tiendasData = tiendasSnapshot.docs.map(doc => ({ 
              id: doc.id, 
              ...doc.data() 
            }));
            console.log("Tiendas encontradas:", tiendasData);

            datatableProfiler.endMeasurement('07c_ProcessData', {
              tiendasCargadas: tiendasData.length
            });

            setTiendas(tiendasData);
          } else {
            // 📊 Medir carga de tiendas para usuario normal
            datatableProfiler.startMeasurement('08_FetchTiendasUser', {
              tiendasAsignadas: adminData.tiendas?.length || 0,
              clienteId
            });

            // Non-admin users only see their assigned stores
            const tiendasIds = adminData.tiendas;
            const batchSize = 30;
            let allTiendasData = [];
            
            for (let i = 0; i < tiendasIds.length; i += batchSize) {
              const batchIds = tiendasIds.slice(i, i + batchSize);
              tiendasQuery = query(
                collection(db, "servicios", currentService.id, "tiendas"),
                where("__name__", "in", batchIds),
                where("cliente", "==", clienteId)
              );

              const tiendasSnapshot = await getDocs(tiendasQuery);
              const tiendasData = tiendasSnapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data() 
              }));
              allTiendasData = [...allTiendasData, ...tiendasData];
              setTiendas(prevTiendas => [...prevTiendas, ...tiendasData]);
            }

            datatableProfiler.endMeasurement('08_FetchTiendasUser', {
              tiendasCargadas: allTiendasData.length,
              batches: Math.ceil(tiendasIds.length / batchSize)
            });
          }

          // 📊 Medir carga de imagen del cliente
          datatableProfiler.startMeasurement('09_FetchClienteImg', {
            clienteId
          });

          // Fetch client image
          const clienteDoc = doc(db, "servicios", currentService.id, "clientes", clienteId);
          const clienteSnap = await getDoc(clienteDoc);
          if (clienteSnap.exists()) {
            setClienteImg(clienteSnap.data().img || "");
          }

          datatableProfiler.endMeasurement('09_FetchClienteImg', {
            imgEncontrada: clienteSnap.exists() && !!clienteSnap.data().img
          });

          // 📊 Finalizar sesión
          datatableProfiler.endSession({
            tiendasCargadas: tiendas.length,
            success: true
          });

        } catch (error) {
          console.error("Error al obtener las tiendas:", error);
          
          // Finalizar sesión con error
          datatableProfiler.endSession({
            error: error.message,
            success: false
          });
        }
      } else {
        console.error("Servicio no definido en el contexto.");
      }
    };

    fetchData();
  }, [clienteId, adminData, currentService.id]);

  // Fetch emails - OPTIMIZADO para cargar en batch
  useEffect(() => {
    const fetchEmails = async () => {
      if (tiendas.length === 0) return;

      // 📊 Iniciar sesión de profiling para emails
      datatableProfiler.startSession('DatatableTiendas_FetchEmails', {
        tiendasCount: tiendas.length
      });

      // 📊 Medir recolección de IDs únicos
      datatableProfiler.startMeasurement('10a_CollectUniqueIds', {
        tiendasCount: tiendas.length
      });

      // Recopilar todos los IDs únicos que necesitan emails
      const allIds = new Set();
      for (const tienda of tiendas) {
        if (tienda.administrativos) {
          tienda.administrativos.forEach(id => allIds.add(id));
        }
        if (tienda.inspectores) {
          tienda.inspectores.forEach(id => allIds.add(id));
        }
      }

      // Filtrar los que no están en caché
      const idsToFetch = Array.from(allIds).filter(id => !emails[id]);

      datatableProfiler.endMeasurement('10a_CollectUniqueIds', {
        totalUniqueIds: allIds.size,
        alreadyCached: allIds.size - idsToFetch.length,
        needsToFetch: idsToFetch.length
      });

      if (idsToFetch.length === 0) {
        datatableProfiler.endSession({
          emailsFetched: 0,
          allCached: true
        });
        return;
      }

      // 📊 Medir carga de emails en batch
      datatableProfiler.startMeasurement('10b_FetchEmailsBatch', {
        idsToFetch: idsToFetch.length
      });

      // Cargar emails en batches de 10 (límite de Firestore para 'in')
      const BATCH_SIZE = 10;
      let emailsFetched = 0;
      const newEmails = {};

      for (let i = 0; i < idsToFetch.length; i += BATCH_SIZE) {
        const batchIds = idsToFetch.slice(i, i + BATCH_SIZE);
        
        try {
          const adminsQuery = query(
            collection(db, "servicios", currentService.id, "admins"),
            where("__name__", "in", batchIds)
          );
          
          const snapshot = await getDocs(adminsQuery);
          snapshot.docs.forEach(doc => {
            const adminData = doc.data();
            newEmails[doc.id] = adminData.correo;
            emailsFetched++;
          });
        } catch (error) {
          console.error(`Error fetching emails batch ${i / BATCH_SIZE + 1}:`, error);
        }
      }

      datatableProfiler.endMeasurement('10b_FetchEmailsBatch', {
        emailsFetched,
        batches: Math.ceil(idsToFetch.length / BATCH_SIZE)
      });

      // 📊 Medir actualización de estado
      datatableProfiler.startMeasurement('10c_UpdateEmailsState', {
        emailsToAdd: Object.keys(newEmails).length
      });

      setEmails(prevEmails => ({ ...prevEmails, ...newEmails }));

      datatableProfiler.endMeasurement('10c_UpdateEmailsState', {
        totalEmailsNow: Object.keys(emails).length + Object.keys(newEmails).length
      });

      // 📊 Finalizar sesión
      datatableProfiler.endSession({
        emailsFetched,
        totalEmailsCached: Object.keys(emails).length + Object.keys(newEmails).length,
        success: true
      });
    };

    fetchEmails();
  }, [tiendas]);

  const getEmailForId = async (id) => {
    try {
      if (currentService && adminData) {
        const adminDocRef = doc(db, "servicios", currentService.id, "admins", id);
        const adminDocSnap = await getDoc(adminDocRef);

        if (adminDocSnap.exists()) {
          const adminData = adminDocSnap.data();
          return adminData.correo;
        }
      }
    }
    catch (error) {
      console.error("Error al obtener datos de administrador:", error);
    }
    return "";
  };

  const handleDelete = async (idsToDelete) => {
    if (!currentService.id) {
      console.error("Servicio no definido en el contexto.");
      return;
    }

    const confirmDelete = window.confirm("¿Estás seguro de que deseas borrar los servicios seleccionados?");
    if (!confirmDelete) {
      return;
    }

    const idsArray = Array.isArray(idsToDelete) ? idsToDelete : [idsToDelete];

    const promises = idsArray.map(async (id) => {
      const tiendaRef = doc(db, "servicios", currentService.id, "tiendas", id);
      const tiendaDoc = await getDoc(tiendaRef);

      if (tiendaDoc.exists()) {
        const newStatus = tiendaDoc.data().borrado ? false : true;
        await setDoc(tiendaRef, { borrado: newStatus }, { merge: true });
        return { id, borrado: newStatus };
      }
      return null;
    });

    try {
      const results = await Promise.all(promises);
      const updatedTiendas = results.filter(result => result !== null);
      setTiendas(prevTiendas =>
        prevTiendas.map(tienda =>
          updatedTiendas.find(updated => updated.id === tienda.id)
            ? { ...tienda, borrado: updatedTiendas.find(updated => updated.id === tienda.id).borrado }
            : tienda
        )
      );
    } catch (err) {
      console.log(err);
    }
  };

  const handleShowDeletedChange = (event) => {
    setShowDeleted(event.target.checked);
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "Acción",
      width: 200,
      renderCell: (params) => {
        return (
          <div className="cellAction">
            <Link to={`/tiendas/update/${encodeURIComponent(params.row.id)}`} style={{ textDecoration: "none" }}>
              <div className="actualizarButton">Actualizar</div>
            </Link>
            {params.row.borrado ? (
              <button className="recoverButton"
                onClick={() => handleDelete(Array.isArray(selectedRows) && selectedRows.length > 0 ? selectedRows : params.row.id)}
              >
                Recuperar
              </button>
            ) : (
              <DeleteIcon
                onClick={() => handleDelete(Array.isArray(selectedRows) && selectedRows.length > 0 ? selectedRows : params.row.id)}
                className="deleteIcon" />
            )}
          </div>
        );
      },
    },
  ];

  const onSelectionModelChange = (newSelectionModel) => {
    setSelectedRows(newSelectionModel);
  };

  const filteredData = tiendas.filter((row) => {
    const searchLower = filter.toLowerCase();
    const matchesSearch = (
      row.numTienda?.toString().toLowerCase().includes(searchLower) ||
      row.nombreTienda?.toLowerCase().includes(searchLower) ||
      row.localizacion?.toLowerCase().includes(searchLower)
    );
    return (
      matchesSearch &&
      (showDeleted ? row.borrado || recoveredItems.includes(row.id) : !row.borrado)
    );
  });

  const dynamicColumns = useMemo(() => {
    return tiendasColumns.map((column) => {
      if (column.field === "administrativos" || column.field === "inspectores") {
        return {
          ...column,
          renderCell: (params) => {
            const ids = params.value || [];
            const emailValues = ids.map((id) => emails[id]);
            return <ArrayCellRenderer values={emailValues} />;
          },
        };
      }
      if (column.field === 'numTienda') {
        return {
          ...column,
          headerName: displayMode === 'localizacion' ? 'Localización' : 'Nombre',
          renderCell: (params) => {
            const value = displayMode === 'localizacion' && params.row.localizacion 
              ? params.row.localizacion 
              : params.row.numTienda;
            return (
              <div style={{
                borderRadius: '10px',
                background: 'rgba(73, 80, 87, 0.27)',
                padding: '5px 10px',
                color: 'black',
                whiteSpace: 'nowrap'
              }}>
                {value}
              </div>
            );
          }
        };
      }
      if (column.field === 'localizacion') {
        return {
          ...column,
          hide: displayMode === 'localizacion'
        };
      }
      return column;
    });
  }, [emails, displayMode]);

  const columns = dynamicColumns.concat(actionColumn);

  return (
    <div className="datatable">
      <div className="datatableTitle datatableTitle-tiendas">
        <div className="headTiendas">
          {clienteImg ? (
            <img src={clienteImg} alt="Logo" className="clienteImg" />
          ) : (
            <StoreMallDirectoryRoundedIcon className="clienteImg" style={{ color: '#fff', fontSize: 40 }} />
          )}
          <h2 className="title-registro-tiendas">
            Servicios de {nombreCliente}
          </h2>
        </div>
        <div className="rightActions">
          <div className="searchContainer">
            <input
              type="text"
              placeholder="Buscar por nombre, localización..."
              className="searchInput"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <SearchIcon className="searchIcon" />
          </div>
          <Link
            to={`/clientes/${encodeURIComponent(clienteId)}/newClienteTienda/${encodeURIComponent(nombreCliente)}`}
            className="link link-right"
          >
            Nuevo servicio
          </Link>
        </div>
      </div>
      {/* Toggle de visualización */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Mostrar:
        </Typography>
        <ToggleButtonGroup
          value={displayMode}
          exclusive
          onChange={handleDisplayModeChange}
          size="small"
        >
          <ToggleButton value="localizacion" sx={{ textTransform: 'none', px: 2 }}>
            Localización
          </ToggleButton>
          <ToggleButton value="nombre" sx={{ textTransform: 'none', px: 2 }}>
            Nombre
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={showDeleted}
            onChange={handleShowDeletedChange}
            color="primary"
          />
        }
        label="Mostrar borrados"
      />
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          navigate(-1);
        }}
      >
        Volver
      </Button>
      <DataGridPro
        className="datagrid"
        rows={filteredData}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        pageSize={100}
        rowsPerPageOptions={[9]}
        checkboxSelection
        onRowSelectionModelChange={onSelectionModelChange}
        localeText={customLocaleText}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
        }}
      />
    </div>
  );
};

export default DatatableTiendasCliente;